import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { restaurantsAPI } from '../lib/api';
import { toast } from 'sonner';
import { CheckCircle2, ArrowRight, Users } from 'lucide-react';
import { LETTER_PILL } from './CycleBoard';

const STATUS_STYLE = {
  draft: 'bg-gray-100 text-gray-700',
  active: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-blue-100 text-blue-700',
  archived: 'bg-amber-50 text-amber-800',
};

const FillBar = ({ filled, capacity }) => {
  const pct = capacity > 0 ? Math.min(100, Math.round((filled / capacity) * 100)) : 0;
  const full = capacity > 0 && filled >= capacity;
  const open = Math.max(capacity - filled, 0);
  return (
    <div className="flex items-center gap-2">
      <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full ${full ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs font-semibold ${full ? 'text-amber-700' : 'text-emerald-700'}`}>
        {filled}/{capacity}{full ? ' · full' : ` · ${open} open`}
      </span>
    </div>
  );
};

const PlacementRow = ({ p }) => {
  const confirmed = (p.ack_status || '').toLowerCase() === 'signed' || (p.status || '').toLowerCase() === 'active';
  const pill = LETTER_PILL[p.letter_status] || LETTER_PILL.not_created;
  return (
    <li className="px-3 py-2 flex items-center gap-3 text-sm" data-testid={`drawer-placement-${p.id}`}>
      <span className="flex-1 min-w-0 truncate text-gray-800">
        {p.student_name}
        <span className="text-gray-400 text-xs"> · {p.position || 'Placement'}</span>
      </span>
      <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${confirmed ? 'text-emerald-600' : 'text-gray-400'}`}>
        <CheckCircle2 className="w-3 h-3" /> {confirmed ? 'Confirmed' : 'Pending'}
      </span>
      <span className={`inline-flex items-center gap-1 text-[11px] font-medium rounded px-1.5 py-0.5 ${pill.cls}`}>
        <pill.Icon className="w-3 h-3" /> {pill.label}
      </span>
    </li>
  );
};

const CycleGroup = ({ g, focused }) => {
  const navigate = useNavigate();
  return (
    <div
      data-testid={`drawer-cycle-group-${g.cycle.id}`}
      className={`rounded-md ${focused ? 'ring-2 ring-ica-gold/60 p-2 -m-2' : ''}`}
    >
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-semibold text-ica-navy text-sm truncate">{g.cycle.name}</span>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${STATUS_STYLE[g.cycle.status] || STATUS_STYLE.draft}`}>{g.cycle.status}</span>
          <span className="text-[11px] text-gray-500 shrink-0">{g.cycle.start_date} → {g.cycle.end_date}</span>
        </div>
        <div className="flex items-center gap-3">
          <FillBar filled={g.filled} capacity={g.capacity} />
          <button
            type="button"
            data-testid={`drawer-open-board-${g.cycle.id}`}
            onClick={() => navigate(`/cycles/${g.cycle.id}`)}
            className="text-[11px] text-ica-navy underline inline-flex items-center gap-0.5 hover:text-ica-gold"
          >
            Open cycle <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
      {g.placements.length === 0 ? (
        <p className="text-xs text-gray-400 italic px-1">No students placed here in this cycle yet.</p>
      ) : (
        <ul className="border rounded-md divide-y bg-white">
          {g.placements.map((p) => <PlacementRow key={p.id} p={p} />)}
        </ul>
      )}
    </div>
  );
};

export const RestaurantPlacementsByCycle = ({ restaurantId, focusCycleId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true);
    restaurantsAPI.placementsByCycle(restaurantId)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load placements'))
      .finally(() => setLoading(false));
  }, [restaurantId]);

  if (loading) return <p className="text-sm text-gray-500 py-6 text-center">Loading placements…</p>;
  if (!data) return null;

  const live = data.groups.filter((g) => g.cycle.status === 'active' || g.cycle.status === 'draft');
  const past = data.groups.filter((g) => g.cycle.status === 'closed' || g.cycle.status === 'archived');
  const pastCount = past.reduce((n, g) => n + g.placements.length, 0);

  return (
    <div className="space-y-6" data-testid="restaurant-placements-by-cycle">
      {live.length === 0 && data.unassigned.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">This restaurant isn't part of any active cycle.</p>
          <p className="text-xs text-gray-400 mt-1">Add it from a cycle's Participants tab to start placing students.</p>
        </div>
      )}
      {live.map((g) => <CycleGroup key={g.cycle.id} g={g} focused={g.cycle.id === focusCycleId} />)}
      {data.unassigned.length > 0 && (
        <div>
          <div className="font-semibold text-gray-600 text-sm mb-2">Not linked to a cycle ({data.unassigned.length})</div>
          <ul className="border rounded-md divide-y bg-white">
            {data.unassigned.map((p) => <PlacementRow key={p.id} p={p} />)}
          </ul>
        </div>
      )}
      {past.length > 0 && (
        <details className="text-xs text-gray-500" data-testid="drawer-past-cycles">
          <summary className="cursor-pointer hover:text-ica-navy">
            Closed / archived cycles ({past.length}) — {pastCount} placement{pastCount === 1 ? '' : 's'}
          </summary>
          <div className="space-y-5 mt-3">
            {past.map((g) => <CycleGroup key={g.cycle.id} g={g} focused={false} />)}
          </div>
        </details>
      )}
    </div>
  );
};

export default RestaurantPlacementsByCycle;
